import { addDays } from "./lecture-plan";

/** Cadence counted in days from the creation day (Day 1 = creation day). */
export const REVISION_OFFSETS = [1, 3, 7, 14, 20];
export const PRACTICE_OFFSETS = [2, 5, 9, 12, 17];
export const CYCLE_LENGTH = 20;

/** Max chapters on one scheduled day. Tighter deadlines allow a heavier day. */
export const CAP_RELAXED = 2; // test is far away (14+ days)
export const CAP_TIGHT = 3; // test is 7 days or less away
export const DAILY_CHAPTER_CAP = CAP_RELAXED;

export function chaptersPerDay(daysUntilTest: number): number {
  return daysUntilTest <= 7 ? CAP_TIGHT : CAP_RELAXED;
}

export type TestChapterInput = {
  subject: string;
  chapter: string;
  weak: boolean;
};

export type TestPlanItem = {
  subject: string;
  chapter: string;
  weak: boolean;
  kind: "revision" | "practice";
  passNumber: number;
  scheduledDate: string;
};

export type TestSchedule = {
  items: TestPlanItem[];
  dropped: string[];
  warning: string | null;
  daysLeft: number;
};

export function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86400000);
}

/**
 * Every cadence date between the creation day and the test date.
 * The pattern repeats in 20-day cycles so there is always a trailing
 * revision/practice rhythm right up to the exam.
 */
export function cadenceDates(
  kind: "revision" | "practice",
  createdOn: string,
  testDate: string,
): string[] {
  const offsets = kind === "revision" ? REVISION_OFFSETS : PRACTICE_OFFSETS;
  const maxDayNumber = daysBetween(createdOn, testDate) + 1; // Day 1 = creation day
  const dates: string[] = [];
  for (let cycle = 0; cycle * CYCLE_LENGTH < maxDayNumber && cycle < 60; cycle += 1) {
    for (const offset of offsets) {
      const dayNumber = cycle * CYCLE_LENGTH + offset;
      if (dayNumber > maxDayNumber) continue;
      dates.push(addDays(createdOn, dayNumber - 1));
    }
  }
  return dates.sort((a, b) => a.localeCompare(b));
}

/**
 * Balanced round-robin: spreads chapters evenly across the given days so no day
 * is overloaded and no day sits empty while others are packed. The cap is
 * respected whenever total capacity allows it.
 */
export function distribute<T>(chapters: T[], days: string[], cap: number): Map<string, T[]> {
  const assignment = new Map<string, T[]>();
  if (!days.length || !chapters.length) return assignment;
  for (const day of days) assignment.set(day, []);

  const perDay = Math.max(cap, Math.ceil(chapters.length / days.length));
  let cursor = 0;
  for (const chapter of chapters) {
    let placed = false;
    for (let step = 0; step < days.length; step += 1) {
      const day = days[(cursor + step) % days.length];
      const list = assignment.get(day)!;
      if (list.length < perDay) {
        list.push(chapter);
        cursor = (cursor + step + 1) % days.length;
        placed = true;
        break;
      }
    }
    if (!placed) assignment.get(days[cursor % days.length])!.push(chapter);
  }
  for (const day of days) {
    if (!assignment.get(day)!.length) assignment.delete(day);
  }
  return assignment;
}

/**
 * Builds the full preparation schedule from the creation day until the test
 * date using the fixed revision (1/3/7/14/20) and practice (2/5/9/12/17)
 * cadence, repeated in cycles. Chapters are spread evenly across the days of
 * each task type; weak chapters are ordered first so they land earliest.
 */
export function buildTestSchedule(
  chapters: TestChapterInput[],
  createdOn: string,
  testDate: string,
  cap = chaptersPerDay(daysBetween(createdOn, testDate)),
): TestSchedule {
  const span = Math.max(0, daysBetween(createdOn, testDate));
  const ordered = [...chapters].sort((a, b) => Number(b.weak) - Number(a.weak));
  const items: TestPlanItem[] = [];
  const dropped: string[] = [];

  for (const kind of ["revision", "practice"] as const) {
    const days = cadenceDates(kind, createdOn, testDate);
    if (!days.length) {
      if (kind === "revision") dropped.push(...ordered.map((c) => c.chapter));
      continue;
    }
    const assignment = distribute(ordered, days, cap);
    const passes = new Map<string, number>();
    for (const [date, list] of assignment) {
      for (const chapter of list) {
        const key = `${chapter.subject}|${chapter.chapter}`;
        const passNumber = (passes.get(key) ?? 0) + 1;
        passes.set(key, passNumber);
        items.push({
          subject: chapter.subject,
          chapter: chapter.chapter,
          weak: chapter.weak,
          kind,
          passNumber,
          scheduledDate: date,
        });
      }
    }
  }

  let warning: string | null = null;
  const revisionDays = cadenceDates("revision", createdOn, testDate).length;
  if (dropped.length) {
    warning = `Your test is only ${span === 0 ? "today" : `${span} ${span === 1 ? "day" : "days"} away`} — there's no room left for a full schedule, so revise your weakest chapters first.`;
  } else if (revisionDays > 0 && ordered.length / revisionDays > cap) {
    warning = `That's ${ordered.length} chapters across ${revisionDays} revision ${revisionDays === 1 ? "day" : "days"} — a heavy pace. Consider trimming a chapter or two.`;
  }

  items.sort(
    (a, b) =>
      a.scheduledDate.localeCompare(b.scheduledDate) ||
      a.kind.localeCompare(b.kind) ||
      a.subject.localeCompare(b.subject) ||
      a.chapter.localeCompare(b.chapter),
  );

  return { items, dropped, warning, daysLeft: span };
}

export type TestItem = TestPlanItem & {
  id: string;
  status: "pending" | "completed";
  completedAt: string | null;
};

export type TestPlan = {
  id: string;
  name: string;
  testDate: string;
  createdAt: string;
  items: TestItem[];
  score: TestScore | null;
  /** Manual override that re-opens score editing after the test-day window. */
  scoreUnlocked: boolean;
};

export type TestScore = {
  obtained: number;
  total: number;
  physics: number | null;
  chemistry: number | null;
  maths: number | null;
  savedAt: string | null;
};

export type TestStatus = "completed" | "today" | "upcoming" | "missed";

/** Status shown on the test card. A test counts as completed once a score exists. */
export function testStatus(plan: TestPlan, today: string): TestStatus {
  if (plan.score) return "completed";
  if (plan.testDate === today) return "today";
  if (plan.testDate > today) return "upcoming";
  return "missed";
}

export const STATUS_LABEL: Record<TestStatus, string> = {
  completed: "Completed",
  today: "Today",
  upcoming: "Upcoming",
  missed: "Missed",
};

/** Tailwind classes per status — 🟢 completed, 🟡 today, 🔵 upcoming, 🔴 missed. */
export const STATUS_STYLE: Record<TestStatus, string> = {
  completed: "border-success/30 bg-success/12 text-success",
  today: "border-warning/30 bg-warning/12 text-warning",
  upcoming: "border-primary/30 bg-primary/12 text-primary",
  missed: "border-destructive/30 bg-destructive/12 text-destructive",
};

export function percentOf(obtained: number, total: number): number {
  return total > 0 ? Math.round((obtained / total) * 1000) / 10 : 0;
}

/** Score entry is open on the test day itself, or whenever manually unlocked. */
export function canEditScore(plan: TestPlan, today: string): boolean {
  return plan.scoreUnlocked || plan.testDate === today;
}

export type ItemState = "completed" | "overdue" | "today" | "locked";

/** Only today is interactive; the future is locked, the past stays completable. */
export function itemState(item: TestItem, today: string): ItemState {
  if (item.status === "completed") return "completed";
  if (item.scheduledDate > today) return "locked";
  if (item.scheduledDate < today) return "overdue";
  return "today";
}

export type TestDay = {
  date: string;
  items: TestItem[];
};

/**
 * Groups items by their scheduled day. Past-due pending items stay on their own
 * day flagged as overdue (they remain completable); the future stays locked.
 */
export function groupTestDays(plan: TestPlan, today: string): { days: TestDay[]; carried: TestItem[] } {
  const carried = plan.items.filter((i) => i.status === "pending" && i.scheduledDate < today);
  const map = new Map<string, TestItem[]>();
  for (const item of plan.items) {
    const list = map.get(item.scheduledDate) ?? [];
    list.push(item);
    map.set(item.scheduledDate, list);
  }
  const days = Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, items]) => ({ date, items }));
  return { days, carried };
}

/** Distinct chapters of a plan, derived from its generated tasks. */
export function planChapters(plan: TestPlan): TestChapterInput[] {
  const map = new Map<string, TestChapterInput>();
  for (const item of plan.items) {
    const key = `${item.subject}|${item.chapter}`;
    if (!map.has(key)) {
      map.set(key, { subject: item.subject, chapter: item.chapter, weak: item.weak });
    }
  }
  return Array.from(map.values());
}

export function planProgress(plan: TestPlan): { done: number; total: number; percent: number } {
  const total = plan.items.length;
  const done = plan.items.filter((i) => i.status === "completed").length;
  return { done, total, percent: total ? Math.round((done / total) * 100) : 0 };
}
